/**
 * Chat Header Component
 *
 * Header bar for the chatbot with title, actions, and status indicators.
 */

import { LitElement, html, css } from 'lit';
import { baseStyles } from '../styles/shared-styles.js';

/**
 * ChatHeader - Header bar component
 * @element chat-header
 * @fires close - When close button is clicked
 * @fires clear - When clear button is clicked
 * @fires start-da-click - When Start now button is clicked
 */
export class ChatHeader extends LitElement {
    static properties = {
        title: { type: String },
        showStartDA: { type: Boolean, attribute: 'show-start-da' },
        isStartDALoading: { type: Boolean, attribute: 'start-da-loading' },
        isHandoverActive: { type: Boolean, attribute: 'handover-active' },
        serviceUnavailable: { type: Boolean, attribute: 'service-unavailable' }
    };

    static styles = [
        baseStyles,
        css`
            :host {
                display: block;
            }

            .header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: var(--spacing-sm);
                padding-left: var(--spacing-md);
                background: #111111;
                color: var(--text-light);
                min-height: 56px;
            }

            .header__left {
                display: flex;
                align-items: center;
                gap: var(--spacing-sm);
            }

            .header__avatar {
                width: 36px;
                height: 36px;
                border-radius: 50%;
                background-color: rgba(255, 255, 255, 0.1);
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
            }

            .header__avatar img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            .header__title {
                font-size: 16px;
                font-weight: 600;
                margin: 0;
            }

            .header__subtitle {
                font-size: 12px;
                opacity: 0.8;
                margin: 0;
            }

            .header__status {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 12px;
                opacity: 0.9;
            }

            .header__status-dot {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background-color: #4caf50;
            }

            .header__status-dot--handover {
                background-color: #ff9800;
                animation: pulse 1.5s infinite;
            }

            .header__status-dot--unavailable {
                background-color: #9ca3af;
            }

            @keyframes pulse {
                0%,
                100% {
                    opacity: 1;
                }
                50% {
                    opacity: 0.5;
                }
            }

            .header__actions {
                display: flex;
                align-items: center;
                gap: var(--spacing-xs);
            }

            .header__btn {
                background: transparent;
                border: none;
                color: var(--text-light);
                padding: 8px;
                border-radius: 50%;
                cursor: pointer;
                transition: background-color var(--transition-fast);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .header__btn:hover {
                background-color: rgba(255, 255, 255, 0.1);
            }

            .header__btn svg {
                width: 20px;
                height: 20px;
            }

            .header__start-da {
                background-color: #111111;
                border: 1px solid rgba(255, 255, 255, 0.3);
                color: var(--text-light);
                padding: 6px 12px;
                border-radius: var(--border-radius);
                font-size: 16px;
                font-weight: 600;
                margin: 0;
                cursor: pointer;
                transition: all var(--transition-fast);
                text-decoration: none;
            }

            .header__start-da:hover {
                background-color: rgba(255, 255, 255, 0.25);
            }
        `
    ];

    constructor() {
        super();
        this.title = 'DAISY';
        this.showStartDA = true;
        this.isStartDALoading = false;
        this.isHandoverActive = false;
        this.serviceUnavailable = false;
    }

    render() {
        return html`
            <header class="header">
                <div class="header__left">
                    <div class="header__avatar">
                        <img src="./Daisy_Updated.png" alt="DAISY" @error=${this._handleImageError} />
                    </div>
                    <div>
                        <h1 class="header__title">${this.title}</h1>
                        <div class="header__status">
                            <span
                                class="header__status-dot ${this.serviceUnavailable
                                    ? 'header__status-dot--unavailable'
                                    : this.isHandoverActive
                                      ? 'header__status-dot--handover'
                                      : ''}"
                                aria-hidden="true"
                            ></span>
                            <span>
                                ${this.serviceUnavailable
                                    ? 'Unavailable'
                                    : this.isHandoverActive
                                      ? 'Connected to agent'
                                      : 'Online'}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="header__actions">
                    ${this.showStartDA
                        ? html`
                              <button
                                  class="header__start-da"
                                  @click=${this._handleStartDA}
                                  ?disabled=${this.isStartDALoading}
                              >
                                  ${this.isStartDALoading ? 'Loading...' : 'Start now'}
                              </button>
                          `
                        : ''}

                    <button class="header__btn" @click=${this._handleClose} title="Close" aria-label="Close chatbot">
                        ${this._renderCloseIcon()}
                    </button>
                </div>
            </header>
        `;
    }

    _renderCloseIcon() {
        return html`
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        `;
    }

    _handleClose() {
        this.dispatchEvent(
            new CustomEvent('close', {
                bubbles: true,
                composed: true
            })
        );
    }

    _handleStartDA() {
        if (this.isStartDALoading) return;

        this.dispatchEvent(
            new CustomEvent('start-da-click', {
                bubbles: true,
                composed: true
            })
        );
    }

    /**
     * Handle image load error
     */
    _handleImageError(e) {
        e.target.style.display = 'none';
    }
}

customElements.define('chat-header', ChatHeader);
